import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { AgentKnowledgeSchema } from "../../../../schemas/Agent/knowledges.schema";
import { ModelConfigWriteSchema } from "../../../../schemas/ModelConfig/model-config.schema";

/**
 * UpdateAgentBodySchema
 *
 * Partial body for PUT /api/agents/:id.
 * All fields are optional — only include the fields you want to change.
 *
 * Naming follows AgentDetailSchema convention:
 * - snake_case for Prisma @map fields: first_name, last_name, short_bio
 * - camelCase for standard Prisma fields: modelConfig, promptJson
 */
export const UpdateAgentBodySchema = z.object({
  // Profile
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  role: z.string().optional(),
  bio: z.string().optional(),
  short_bio: z.string().optional(),
  tags: z.string().optional(),
  gender: z.string().optional(),

  // AI configuration — model validated against known enum values to prevent DB-level errors
  model: z
    .string()
    .refine((val) => (DbEnum.AGENT_MODEL_VALUES as readonly string[]).includes(val), {
      message: "Invalid model value"
    })
    .nullable()
    .optional(),
  modelConfig: ModelConfigWriteSchema.nullable().optional(),

  // Prompt
  promptJson: z.unknown().optional(),
  productionPromptVersionId: z.string().nullable().optional(),
  knowledges: z.array(AgentKnowledgeSchema).optional(),

  // Legacy prompt fields (preserved until full TipTap migration)
  explanation: z.string().nullable().optional(),
  objective: z.string().nullable().optional(),
  tone: z.string().nullable().optional(),
  voicePrompt: z.string().nullable().optional(),
  behaviour: z.array(z.string()).optional(),

  // Channel formats
  format: z.array(z.string()).optional(),
  phoneFormat: z.array(z.string()).optional(),
  whatsappFormat: z.array(z.string()).optional(),
  gmailFormat: z.array(z.string()).optional(),
  outlookFormat: z.array(z.string()).optional(),
  slackFormat: z.array(z.string()).optional(),
  smsFormat: z.array(z.string()).optional(),
  teamsFormat: z.array(z.string()).optional(),
  messengerFormat: z.array(z.string()).optional(),
  instagramFormat: z.array(z.string()).optional(),
  telegramFormat: z.array(z.string()).optional(),
  twilioFormat: z.array(z.string()).optional(),
  apiFormat: z.array(z.string()).optional(),
  wordFormat: z.array(z.string()).optional(),

  // Runtime
  cacheable: z.boolean().optional(),
  cacheTtl: z.enum(["5m", "1h"]).nullable().optional(),
  playgroundFirstMessage: z.string().nullable().optional()
});

export type UpdateAgentBody = z.infer<typeof UpdateAgentBodySchema>;
